// CareLogix™ ORACLE — Acknowledgement Gate v3 (Phase 33 §13 + server-backed v1)
// Storage: oracle_acknowledged_v3 + oracle_acknowledged_version (e.g. "ORACLE_V1").
// Server: persisted to Mongo collection `oracle_acknowledgements` via the
//         /api/oracle/acknowledgement/* router. When the server's current
//         version differs from the locally stored version, the gate re-shows.
import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { PALETTE } from "@/components/animation/MotionShared";
import OraclePrivacyRiskSettings from "@/components/gate/OraclePrivacyRiskSettings";
import { personalizationApi } from "@/api/personalizationClient";
import {
  ORACLE_ACK_V3_KEY,
  ORACLE_ACK_V1_KEY,
  acceptServer,
  fetchCurrentVersion,
  isAcknowledgedV3 as isAcknowledgedV3Combined,
  resolveUserId,
  setAcknowledgedV3Local,
  setAcknowledgedThisSession,
} from "@/api/oracleAcknowledgementClient";

// Backwards-compatible exports preserved for any older imports.
export { ORACLE_ACK_V3_KEY, ORACLE_ACK_V1_KEY };
export const isAcknowledgedV3 = isAcknowledgedV3Combined;
export function setAcknowledgedV3(v, version) { setAcknowledgedV3Local(v, version); }

async function tryPersistToServer(value, scope = "public") {
  // Server-of-truth: ORACLE_ACKNOWLEDGEMENT_PERSISTENCE™ Mongo collection.
  let serverResult = null;
  try {
    const userId = resolveUserId(scope);
    const r = await acceptServer(userId, scope);
    serverResult = r;
  } catch (_) { /* network hiccup → local only */ }
  // Best-effort: also patch the legacy personalization profile if signed-in member.
  try {
    await personalizationApi.patch({ oracle_acknowledged_v3: !!value, oracle_ack_v3_at: new Date().toISOString() });
  } catch (_) { /* ignore */ }
  return serverResult;
}

const BADGES = [
  { label: "LIVE",              color: PALETTE.greenAccent },
  { label: "VERIFIED",          color: PALETTE.gold },
  { label: "HUMAN-IN-THE-LOOP", color: PALETTE.amber },
  { label: "COMPLIANCE LOCK™",  color: PALETTE.emerald },
];

const CAPABILITIES = [
  { icon: "📈", label: "Market Intelligence" },
  { icon: "🔮", label: "Predictive Analytics" },
  { icon: "🏛",  label: "Grant Intelligence" },
  { icon: "💰", label: "Funding Intelligence" },
  { icon: "🚀", label: "Startup Intelligence" },
  { icon: "🏥", label: "Healthcare Investment Intelligence" },
  { icon: "🌍", label: "Global Opportunity Monitoring" },
  { icon: "⚖",  label: "Defensive Compliance" },
];

const NOT_LIST = [
  "Guaranteed outcomes",
  "Financial advice",
  "Autonomous trading",
  "Autonomous wagering",
  "Insider trading or MNPI handling",
  "Market manipulation assistance",
];

export default function OracleAcknowledgementGateV3({ onAccept, testid = "oracle-ack-v3", scope = "public" }) {
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [persisting, setPersisting] = useState(false);
  const [currentVersion, setCurrentVersion] = useState("ORACLE_V1");

  // Pull the current server version on mount so the version pill is truthful
  // even when re-display was caused by a version bump.
  useEffect(() => {
    let cancelled = false;
    fetchCurrentVersion().then((v) => { if (!cancelled && v) setCurrentVersion(v); }).catch(() => {});
    return () => { cancelled = true; };
  }, []);

  const handleAccept = async () => {
    setPersisting(true);
    try {
      // 1) Persist server-side first (source of truth — Mongo collection).
      const result = await tryPersistToServer(true, scope);
      const versionConfirmed = result?.version || currentVersion || "ORACLE_V1";
      // 2) Mirror locally for fast-path future loads (when ORACLE_ALWAYS_ACKNOWLEDGE is OFF).
      setAcknowledgedV3Local(true, versionConfirmed);
      // 3) Session marker — required by ORACLE_ALWAYS_ACKNOWLEDGE™. Cleared on tab close.
      setAcknowledgedThisSession(true);
    } finally {
      setPersisting(false);
    }
    if (typeof onAccept === "function") {
      try { onAccept(); } catch (_) { /* ignore */ }
    }
  };

  return (
    <main style={S.shell} data-testid={testid} role="dialog" aria-modal="true" aria-labelledby={`${testid}-title`}>
      <div style={S.bgGlow} aria-hidden="true" />
      <motion.section
        style={S.card}
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, ease: "easeOut" }}
      >
        <header style={S.header}>
          <span style={S.eyebrow} data-testid={`${testid}-version-pill`}>GLOBAL INTELLIGENCE CONTINUITY™ · {currentVersion}</span>
          <h1 style={S.title} id={`${testid}-title`} data-testid={`${testid}-title`}>CARELOGIX™ ORACLE</h1>
          <p style={S.subtitle} data-testid={`${testid}-subtitle`}>
            CareLogix™ ORACLE provides market intelligence, predictive analytics, grant/funding intelligence,
            startup intelligence, healthcare investment intelligence, and global opportunity monitoring.
            Outputs are educational, informational, and decision-support only. No guaranteed outcomes.
            Human confirmation required.
          </p>
        </header>

        <div style={S.badgeRow}>
          {BADGES.map((b) => (
            <span key={b.label} style={{ ...S.badge, color: b.color, borderColor: b.color, boxShadow: `0 0 16px ${b.color}33 inset` }}>
              <span style={{ ...S.badgeDot, background: b.color }} aria-hidden="true" />
              {b.label}
            </span>
          ))}
        </div>

        <h3 style={S.sectionTitle}>ORACLE MAY PROCESS</h3>
        <ul style={S.capGrid}>
          {CAPABILITIES.map((c) => (
            <li key={c.label} style={S.capPill}>
              <span style={S.capIcon} aria-hidden="true">{c.icon}</span>
              <span style={S.capLabel}>{c.label}</span>
            </li>
          ))}
        </ul>

        <div style={S.notBlock} data-testid={`${testid}-not-block`}>
          <h3 style={S.notTitle}>ORACLE™ DOES NOT</h3>
          <ul style={S.notList}>
            {NOT_LIST.map((l) => (
              <li key={l} style={S.notItem}>
                <span style={{ ...S.bullet, color: PALETTE.red }} aria-hidden="true">✕</span>{l}
              </li>
            ))}
          </ul>
        </div>

        <div style={S.complianceLock}>
          <span style={S.complianceTitle}>COMPLIANCE LOCK™</span>
          <ul style={S.complianceList}>
            <li>Past performance is not predictive of future outcomes.</li>
            <li>Public data only. MNPI rejected by design.</li>
            <li>Human-in-the-loop required for every consequential decision.</li>
          </ul>
        </div>

        <div style={S.ctaRow}>
          <button
            type="button"
            onClick={handleAccept}
            disabled={persisting}
            style={S.ctaPrimary}
            data-testid={`${testid}-continue`}
            aria-label="Continue to CareLogix ORACLE"
          >
            <span style={{ marginRight: 10 }} aria-hidden="true">🟨</span>
            {persisting ? "Saving…" : "CONTINUE TO ORACLE™"}
            <span style={{ marginLeft: 10 }} aria-hidden="true">→</span>
          </button>
          <button
            type="button"
            onClick={() => setSettingsOpen(true)}
            style={S.ctaSecondary}
            data-testid={`${testid}-settings`}
          >
            <span style={{ marginRight: 8 }} aria-hidden="true">⚙</span>
            PRIVACY &amp; RISK SETTINGS
          </button>
        </div>
      </motion.section>

      <OraclePrivacyRiskSettings
        open={settingsOpen}
        onClose={() => setSettingsOpen(false)}
        testid={`${testid}-privacy-risk-drawer`}
      />
    </main>
  );
}

export function useV3AckListener(setAcceptedFn) {
  useEffect(() => {
    function syncStorage(ev) {
      if (!ev || ev.key === ORACLE_ACK_V3_KEY || ev.key === null) {
        setAcceptedFn(isAcknowledgedV3());
      }
    }
    function syncCustom(ev) { setAcceptedFn(Boolean(ev?.detail?.value ?? isAcknowledgedV3())); }
    if (typeof window !== "undefined") {
      window.addEventListener("storage", syncStorage);
      window.addEventListener("oracle:acknowledged-v3-changed", syncCustom);
    }
    return () => {
      if (typeof window !== "undefined") {
        window.removeEventListener("storage", syncStorage);
        window.removeEventListener("oracle:acknowledged-v3-changed", syncCustom);
      }
    };
  }, [setAcceptedFn]);
}

const S = {
  shell:    { position: "fixed", inset: 0, zIndex: 9990, background: "radial-gradient(circle at 50% 0%, rgba(15,45,30,0.55) 0%, rgba(4,6,8,1) 65%)", overflowY: "auto", padding: "24px 16px 48px", display: "flex", alignItems: "flex-start", justifyContent: "center", fontFamily: "'Figtree', 'Inter', system-ui, sans-serif" },
  bgGlow:   { position: "absolute", inset: 0, backgroundImage: "radial-gradient(ellipse 600px 320px at 50% 6%, rgba(197,160,89,0.10), transparent 65%)", pointerEvents: "none" },
  card:     { position: "relative", width: "100%", maxWidth: 880, background: "linear-gradient(180deg, rgba(11,15,20,0.96), rgba(6,18,12,0.96))", border: `1px solid ${PALETTE.goldGlow}`, borderRadius: 24, padding: "30px clamp(20px, 4vw, 44px) 24px", color: PALETTE.slate3, display: "flex", flexDirection: "column", gap: 14, boxShadow: "0 30px 80px rgba(0,0,0,0.55), inset 0 0 60px rgba(197,160,89,0.05)" },
  header:   { display: "flex", flexDirection: "column", alignItems: "center", textAlign: "center", gap: 6 },
  eyebrow:  { color: PALETTE.gold, fontFamily: "'Space Mono', monospace", fontSize: 11, letterSpacing: "0.32em" },
  title:    { margin: 0, color: PALETTE.goldBright, fontSize: "clamp(24px, 4.2vw, 38px)", letterSpacing: "0.10em", fontWeight: 800 },
  subtitle: { margin: 0, color: PALETTE.slate3, fontSize: "clamp(13px, 1.6vw, 15px)", maxWidth: 720, lineHeight: 1.6 },
  badgeRow: { display: "flex", flexWrap: "wrap", justifyContent: "center", gap: 8 },
  badge:    { display: "inline-flex", alignItems: "center", gap: 8, background: "rgba(11,15,20,0.7)", border: "1px solid", padding: "6px 12px", borderRadius: 999, fontFamily: "'Space Mono', monospace", fontSize: 10.5, letterSpacing: "0.14em" },
  badgeDot: { width: 7, height: 7, borderRadius: "50%", display: "inline-block" },
  sectionTitle: { margin: 0, color: PALETTE.gold, fontFamily: "'Space Mono', monospace", fontSize: 11.5, letterSpacing: "0.18em", textAlign: "center" },
  capGrid:  { margin: 0, padding: 0, listStyle: "none", display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 8 },
  capPill:  { display: "flex", alignItems: "center", gap: 10, padding: "8px 12px", background: "rgba(197,160,89,0.05)", border: `1px solid ${PALETTE.goldGlow}`, borderRadius: 12 },
  capIcon:  { fontSize: 18, lineHeight: 1 },
  capLabel: { fontSize: 13, color: PALETTE.slate3 },
  notBlock:  { background: "rgba(239,68,68,0.06)", border: `1px solid ${PALETTE.red}55`, borderRadius: 14, padding: 12, display: "flex", flexDirection: "column", gap: 6 },
  notTitle:  { margin: 0, color: PALETTE.red, fontFamily: "'Space Mono', monospace", fontSize: 11, letterSpacing: "0.18em", textAlign: "center" },
  notList:   { margin: 0, padding: 0, listStyle: "none", display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 4 },
  notItem:   { display: "flex", alignItems: "center", gap: 6, color: PALETTE.slate3, fontSize: 12.5 },
  bullet:    { fontFamily: "'Space Mono', monospace", fontSize: 12, width: 12, textAlign: "center" },
  complianceLock: { background: "rgba(252,211,77,0.05)", border: `1px solid ${PALETTE.amber}`, borderRadius: 14, padding: 12 },
  complianceTitle: { color: PALETTE.amber, fontFamily: "'Space Mono', monospace", fontSize: 12, letterSpacing: "0.18em", fontWeight: 800, textAlign: "center", display: "block" },
  complianceList:  { margin: "6px 0 0 0", padding: 0, listStyle: "none", display: "flex", flexDirection: "column", gap: 4, color: PALETTE.slate3, fontSize: 12.5, lineHeight: 1.4 },
  ctaRow:   { display: "flex", flexDirection: "column", gap: 8, marginTop: 4 },
  ctaPrimary:{ appearance: "none", width: "100%", background: "linear-gradient(180deg, #fde68a 0%, #c5a059 100%)", color: "#0b0f14", border: "1px solid #c5a059", boxShadow: "0 12px 30px rgba(197,160,89,0.30), inset 0 -2px 0 rgba(0,0,0,0.15)", padding: "14px 18px", borderRadius: 14, fontWeight: 800, fontFamily: "'Space Mono', monospace", letterSpacing: "0.14em", fontSize: 14, cursor: "pointer" },
  ctaSecondary:{ appearance: "none", width: "100%", background: "transparent", color: PALETTE.gold, border: `1px solid ${PALETTE.goldGlow}`, padding: "12px 16px", borderRadius: 14, fontWeight: 700, fontFamily: "'Space Mono', monospace", letterSpacing: "0.12em", fontSize: 12.5, cursor: "pointer" },
};
